(() => {
  const TAG_NAME = "concentrate-v2-skio-picker";

  if (customElements.get(TAG_NAME)) return;

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>'"]/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      "'": "&#39;",
      '"': "&quot;",
    })[character]);

  class ConcentrateV2SkioPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.productRoot = this.closest(".sv-pdp__product-section");
      this.product = this.readProduct();
      this.currentVariant = this.readCurrentVariant();
      this.selectedPlanId = "";

      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onInitialPurchaseMode = this.onInitialPurchaseMode.bind(this);
      this.onClick = this.onClick.bind(this);
      document.addEventListener("variantChanged", this.onVariantChanged);
      this.productRoot?.addEventListener(
        "cv2:initial-purchase-mode",
        this.onInitialPurchaseMode,
      );
      this.addEventListener("click", this.onClick);

      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener("variantChanged", this.onVariantChanged);
      this.productRoot?.removeEventListener(
        "cv2:initial-purchase-mode",
        this.onInitialPurchaseMode,
      );
      this.removeEventListener("click", this.onClick);
      window.clearTimeout(this.selectionAnimationTimer);
    }

    readProduct() {
      const productJson = document.getElementById(this.dataset.productJsonId);
      if (!productJson) return null;

      try {
        return JSON.parse(productJson.textContent);
      } catch (_) {
        return null;
      }
    }

    readCurrentVariant() {
      const variantSelect = this.form?.querySelector('[name="id"]');
      const variantId = variantSelect?.value;
      return this.product?.variants?.find(
        (variant) => String(variant.id) === String(variantId),
      ) || this.product?.variants?.[0] || null;
    }

    alpineData() {
      if (!this.productRoot) return null;

      try {
        if (typeof window.Alpine?.$data === "function") {
          return window.Alpine.$data(this.productRoot);
        }
      } catch (_) {}

      return this.productRoot._x_dataStack?.[0] || null;
    }

    availablePlans() {
      const allocations = this.currentVariant?.selling_plan_allocations || [];
      const skioGroups = (this.product?.selling_plan_groups || []).filter(
        (group) => group.app_id === "SKIO",
      );
      const plansById = new Map();

      skioGroups.forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          plansById.set(String(plan.id), plan);
        });
      });

      return allocations
        .map((allocation) => {
          const id = String(allocation.selling_plan_id);
          const plan = plansById.get(id);
          return plan ? { id, allocation, plan } : null;
        })
        .filter(Boolean);
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || "USD";
      const locale = document.documentElement.lang || "en-US";

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format((Number(cents) || 0) / 100);
    }

    discountPercent(plan) {
      const regularPrice = Number(this.currentVariant?.price) || 0;
      const subscriptionPrice = Number(plan?.allocation?.price) || regularPrice;
      if (!regularPrice || subscriptionPrice >= regularPrice) return 0;
      return Math.round(((regularPrice - subscriptionPrice) / regularPrice) * 100);
    }

    selectedPlan(plans = this.availablePlans()) {
      return plans.find((plan) => plan.id === this.selectedPlanId) || plans[0] || null;
    }

    isSubscribed() {
      return this.alpineData()?.purchaseMode === "subscribe";
    }

    ensureSellingPlanInput() {
      if (!this.form) return null;

      let input = this.form.querySelector('[name="selling_plan"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "selling_plan";
        this.form.append(input);
      }
      return input;
    }

    setFormPurchase(mode, planId = "") {
      const formState = this.alpineData();
      const isSubscribe = mode === "subscribe" && planId;
      const sellingPlanInput = this.ensureSellingPlanInput();

      if (formState) {
        if (isSubscribe) {
          formState.selectedSellingPlanId = String(planId);
          formState.setPurchaseMode?.("subscribe");
        } else {
          // The existing Concentrate V2 form calls the OTP state "onetime".
          formState.setPurchaseMode?.("onetime");
          formState.selectedSellingPlanId = "";
        }
      }

      if (sellingPlanInput) {
        sellingPlanInput.value = isSubscribe ? String(planId) : "";
        sellingPlanInput.disabled = !isSubscribe;
      }

      this.dispatchEvent(
        new CustomEvent("skio::update-selling-plan", {
          bubbles: true,
          composed: true,
          detail: { sellingPlanId: isSubscribe ? String(planId) : null },
        }),
      );
    }

    beginSelectionAnimation() {
      window.clearTimeout(this.selectionAnimationTimer);
      this.classList.add("is-animating");
      this.selectionAnimationTimer = window.setTimeout(() => {
        this.classList.remove("is-animating");
      }, 180);
    }

    selectSubscribe(planId) {
      const plan = this.availablePlans().find((item) => item.id === String(planId));
      if (!plan) return this.selectOtp();

      this.selectedPlanId = plan.id;
      this.setFormPurchase("subscribe", plan.id);
      this.beginSelectionAnimation();
      this.render();
    }

    selectOtp() {
      this.setFormPurchase("otp");
      this.beginSelectionAnimation();
      this.render();
    }

    onVariantChanged(event) {
      const variant = this.product?.variants?.find(
        (item) => String(item.id) === String(event.detail?.variantId),
      );
      if (!variant) return;

      this.currentVariant = variant;
      const plans = this.availablePlans();
      const selectedPlan = this.selectedPlan(plans);
      this.selectedPlanId = selectedPlan?.id || "";

      if (!selectedPlan) {
        this.setFormPurchase("otp");
      } else if (this.isSubscribed()) {
        this.setFormPurchase("subscribe", selectedPlan.id);
      } else {
        this.setFormPurchase("otp");
      }

      this.render();
    }

    onInitialPurchaseMode(event) {
      const variant = this.product?.variants?.find(
        (item) => String(item.id) === String(event.detail?.variantId),
      );
      if (variant) this.currentVariant = variant;

      const plans = this.availablePlans();
      const selectedPlan = this.selectedPlan(plans);
      this.selectedPlanId = selectedPlan?.id || "";

      if (event.detail?.purchaseMode === "subscribe" && selectedPlan) {
        this.setFormPurchase("subscribe", selectedPlan.id);
      } else {
        this.setFormPurchase("otp");
      }

      this.render();
    }

    onClick(event) {
      const control = event.target.closest("[data-cv2-skio-action]");
      if (!control || !this.contains(control)) return;

      if (control.dataset.cv2SkioAction === "subscribe") {
        this.selectSubscribe(control.dataset.planId);
      } else if (control.dataset.cv2SkioAction === "otp") {
        this.selectOtp();
      }
    }

    refresh() {
      const plans = this.availablePlans();
      const selectedPlan = this.selectedPlan(plans);
      this.selectedPlanId = selectedPlan?.id || "";

      if (!selectedPlan) this.setFormPurchase("otp");
      this.render();
    }

    render() {
      if (!this.currentVariant) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      const plans = this.availablePlans();
      const selectedPlan = this.selectedPlan(plans);
      const isSubscribed = Boolean(selectedPlan) && this.isSubscribed();
      const regularPrice = this.formatMoney(this.currentVariant.price);
      const subscriptionPrice = selectedPlan
        ? this.formatMoney(selectedPlan.allocation.price)
        : "";
      const compareAtPrice = Number(this.currentVariant.compare_at_price) || 0;
      const otpOriginalPrice = compareAtPrice > Number(this.currentVariant.price)
        ? this.formatMoney(compareAtPrice)
        : "";
      const discount = this.discountPercent(selectedPlan);
      const subscriptionSelected = isSubscribed ? " is-selected" : "";
      const otpSelected = isSubscribed ? "" : " is-selected";
      const benefits = isSubscribed
        ? String(selectedPlan?.plan?.description || "")
          .split(/\r?\n/)
          .map((benefit) => benefit.trim())
          .filter(Boolean)
        : [];
      const benefitsMarkup = benefits.length ? `
        <span class="cv2-skio__benefits" aria-label="Subscription benefits">
          ${benefits.map((benefit) => `
            <span class="cv2-skio__benefit">
              <svg class="cv2-skio__benefit-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                <path d="M2.5 8.25 6.25 12 13.5 4.75" stroke="#2C8B57" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>${escapeHtml(benefit)}</span>
            </span>
          `).join("")}
        </span>
      ` : "";

      this.innerHTML = `
        ${selectedPlan ? `
          <button type="button" class="cv2-skio__card cv2-skio__card--subscribe${subscriptionSelected}" data-cv2-skio-action="subscribe" data-plan-id="${escapeHtml(selectedPlan.id)}" aria-pressed="${isSubscribed}">
            <span class="cv2-skio__card-row">
              <span class="cv2-skio__copy">
                <span class="cv2-skio__title">Subscribe &amp; Save</span>
                ${discount > 0 ? `<span class="cv2-skio__save sv-pdp__variant-badge sv-pdp__variant-badge--pill">Save ${discount}% Off</span>` : ""}
              </span>
              <span class="cv2-skio__prices">
                <s class="cv2-skio__original-price">${escapeHtml(regularPrice)}</s>
                <span class="cv2-skio__price">${escapeHtml(subscriptionPrice)}</span>
              </span>
            </span>
            ${benefitsMarkup}
          </button>
        ` : ""}
        <button type="button" class="cv2-skio__card cv2-skio__card--otp${otpSelected}" data-cv2-skio-action="otp" aria-pressed="${!isSubscribed}">
          <span class="cv2-skio__title">One Time Purchase</span>
          <span class="cv2-skio__prices">
            ${otpOriginalPrice ? `<s class="cv2-skio__original-price">${escapeHtml(otpOriginalPrice)}</s>` : ""}
            <span class="cv2-skio__price">${escapeHtml(regularPrice)}</span>
          </span>
        </button>
        ${isSubscribed && plans.length > 1 ? `
          <div class="cv2-skio__frequency" aria-label="Subscription frequency">
            ${plans.map((plan) => `
              <button type="button" class="cv2-skio__frequency-option${plan.id === selectedPlan.id ? " is-selected" : ""}" data-cv2-skio-action="subscribe" data-plan-id="${escapeHtml(plan.id)}" aria-pressed="${plan.id === selectedPlan.id}">
                ${escapeHtml(plan.plan.name)}
              </button>
            `).join("")}
          </div>
        ` : ""}
      `;
    }
  }

  customElements.define(TAG_NAME, ConcentrateV2SkioPicker);
})();
